import React, { useState, useEffect } from 'react';
import PaymentPopup from '../payments/PaymentPopup';
import { getPayabliTokens } from '../../../utils/apis';
import { IPaymentObject } from '../../../utils/types';
import { validatePhoneNumber, getPhoneNumberForBackend, validateZipCode, validateStateAbbreviation, validateCompleteAddress, formatCompleteAddress } from '../../../utils/utils';
import DonationHeader from './DonationHeader';
import DonationAmountSelector from './DonationAmountSelector';
import DonorInformation from './DonorInformation';
import { FailurePopup } from '../../shared/FailurePopup';
import Footer from '../../shared/Footer';
import DonationSuccessModal from './DonationSuccessModal';

interface DonationFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  specialDesignation: string;
  amount: number;
}

interface DonationFormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

interface PayabliConfig {
  token: string;
  entry: string;
}

const Donate: React.FC = () => {
  const [formData, setFormData] = useState<DonationFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    specialDesignation: '',
    amount: 0
  });
  const [errors, setErrors] = useState<DonationFormErrors>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [payabliConfig, setPayabliConfig] = useState<PayabliConfig | null>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  
  // Store submitted form data for success modal
  const [submittedFormData, setSubmittedFormData] = useState<DonationFormData | null>(null);
  const [paymentReferenceId, setPaymentReferenceId] = useState<string>('');
  
  // Add email validation state
  const [isEmailValid, setIsEmailValid] = useState(false);

  // Fetch Payabli configuration on component mount
  useEffect(() => {
    const fetchPayabliConfig = async () => {
      try {
        setIsLoadingConfig(true);
        // Using a default provider ID for donations - you may need to adjust this
        const response = await getPayabliTokens('donation');
        setPayabliConfig({
          token: response?.data?.token,
          entry: response?.data?.entry
        });
      } catch (error) {
        console.error('Error fetching Payabli configuration:', error);
      } finally {
        setIsLoadingConfig(false);
      }
    };

    fetchPayabliConfig();
  }, []);

  // Email validation function
  const validateEmailFormat = (email: string): boolean => {
    if (!email.trim()) return false;
    
    // Comprehensive email validation regex
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    
    if (!emailRegex.test(email)) return false;
    
    // Check length constraints
    if (email.length > 254) return false;
    
    const localPart = email.split('@')[0];
    if (localPart.length > 64) return false;
    
    // Check for consecutive dots
    if (email.includes('..')) return false;
    
    // Check for dots at start/end of local part
    if (localPart.startsWith('.') || localPart.endsWith('.')) return false;
    
    // Check for spaces
    if (email.includes(' ')) return false;
    
    // Check for valid TLD
    const domain = email.split('@')[1];
    const tld = domain?.split('.').pop();
    if (!tld || tld.length < 2) return false;
    
    // Check for minimum domain length
    if (domain && domain.length < 3) return false;
    
    // Check for valid domain format (no consecutive hyphens, no hyphens at start/end)
    if (domain && (domain.includes('--') || domain.startsWith('-') || domain.endsWith('-'))) return false;
    
    return true;
  };

  const validateForm = (): boolean => {
    const newErrors: DonationFormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmailFormat(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    // Phone number validation - must have exactly 10 digits
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!validatePhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Phone number must have 10 digits';
    }

    // Address validation
    if (!formData.streetAddress.trim()) {
      newErrors.streetAddress = 'Street address is required';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    } else if (!validateStateAbbreviation(formData.state)) {
      newErrors.state = 'State must be 2 characters';
    }
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    } else if (!validateZipCode(formData.zipCode)) {
      newErrors.zipCode = 'ZIP code must be 5 digits or 5+4 format';
    }
    
    if (formData.amount <= 0) {
      // Amount validation is handled in DonationAmountSelector
      return false;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field as keyof DonationFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
    
    // Update email validation state when email changes
    if (field === 'email') {
      setIsEmailValid(validateEmailFormat(value));
    }
  };

  const handleDonateClick = () => {
    if (!validateForm()) {
      return;
    }

    if (!payabliConfig?.token || !payabliConfig?.entry) {
      setErrorMsg('Payment system is not configured. Please try again later.');
      return;
    }

    setIsProcessing(true);
    setShowPaymentPopup(true);
  };

  const closePaymentPopup = () => {
    setShowPaymentPopup(false);
    setIsProcessing(false);
  };

  const handlePaymentSuccess = (message: string, referenceId?: string) => {
    // Store the current form data before resetting
    setSubmittedFormData({ ...formData });

    console.log('🔍 [DEBUG] referenceId:', referenceId);
    
    // Store payment reference ID if provided, otherwise generate one
    if (referenceId && referenceId.trim()) {
      setPaymentReferenceId(referenceId);
    }
    
    setShowSuccessPopup(true);
    closePaymentPopup();
    // Reset form after successful donation
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      streetAddress: '',
      city: '',
      state: '',
      zipCode: '',
      specialDesignation: '',
      amount: 0
    });
    setIsEmailValid(false);
  };

  const handlePaymentError = (message: string) => {
    setErrorMsg(`Payment failed: ${message}`);
    closePaymentPopup();
  };

  // Check if form is valid for enabling pay button
  const isFormValid = () => {
    return formData.firstName.trim() !== '' && 
           formData.lastName.trim() !== '' && 
           formData.email.trim() !== '' && 
           validateEmailFormat(formData.email) &&
           formData.phoneNumber.trim() !== '' &&
           validatePhoneNumber(formData.phoneNumber) &&
           validateCompleteAddress(formData.streetAddress, formData.city, formData.state, formData.zipCode) &&
           formData.amount > 0;
  };

  // Create payment object for PaymentPopup
  const createPaymentObject = (): IPaymentObject => {
    // Combine address fields into a single formatted address string
    const formattedAddress = formatCompleteAddress(formData.streetAddress, formData.city, formData.state, formData.zipCode);
    
    const paymentObject: IPaymentObject = {
      amount: formData.amount,
      startDate: new Date().toISOString().split('T')[0], // Today's date
      frequency: 'ONETIME',
      isRecurring: false, // Always false for donations
      guarantorLastName: formData.lastName,
      guarantorNumber: `donation_${Date.now()}`, // Generate unique identifier
      identityId: `donation_${Date.now()}`,
      token: payabliConfig?.token || '',
      entityId: payabliConfig?.entry || '',
      paymentType: 'DONATION',
      donationData: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: getPhoneNumberForBackend(formData.phoneNumber), // Format as +1XXXXXXXXXX
        address: formattedAddress,
        specialDesignation: formData.specialDesignation,
      }
    };
    return paymentObject;
  };

  if (isLoadingConfig) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment system...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F5F9]">
      <DonationSuccessModal
        isOpen={showSuccessPopup && !!submittedFormData && !!paymentReferenceId}
        onClose={() => {
          setShowSuccessPopup(false);
          // Clear stored data when modal is closed
          setSubmittedFormData(null);
          setPaymentReferenceId('');
          window.location.href = 'https://stlukehealthcare.org/donate/';
        }}
        email={submittedFormData?.email || ''}
        paymentReferenceId={paymentReferenceId}
        amount={submittedFormData?.amount || 0}
      />
      <FailurePopup
        message={errorMsg}
        isOpen={!!errorMsg}
        onClose={() => {
          setErrorMsg('');
        }}
      />
      <DonationHeader />
      <div className="flex flex-col items-center justify-center p-4 pt-8 lg:p-8 w-full max-w-[1440px] mx-auto sm:p-2">
        <DonationAmountSelector 
          amount={formData.amount} 
          setAmount={(amount) => {
            setFormData(prev => ({ ...prev, amount }));
          }} 
        />
      </div>

      <div className="flex flex-col items-center justify-center p-4 pt-0 lg:p-8 lg:pt-0 w-full max-w-[1440px] mx-auto sm:p-2">
        <DonorInformation
          firstName={formData.firstName}
          lastName={formData.lastName}
          email={formData.email}
          phoneNumber={formData.phoneNumber}
          streetAddress={formData.streetAddress}
          city={formData.city}
          state={formData.state}
          zipCode={formData.zipCode}
          specialDesignation={formData.specialDesignation}
          onFieldChange={handleFieldChange}
          errors={errors}
          onValidationChange={(isValid) => {
            if (isValid !== undefined) {
              setIsEmailValid(isValid);
            }
          }}
        />
        
        {/* Pay Button */}
        <div className="w-full max-w-2xl mt-6">
          <button
            type="button"
            onClick={handleDonateClick}
            disabled={!isFormValid() || isProcessing || !payabliConfig?.token || !payabliConfig?.entry}
            className="w-full bg-[#153B4E] text-white font-semibold py-4 px-8 rounded-xl text-lg uppercase tracking-wide hover:shadow-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
          >
            {isProcessing ? 'Processing...' : 'Pay Now'}
          </button>
        </div>
      </div>

      {/* Payment Popup */}
      {showPaymentPopup && (
        <PaymentPopup
          closePopup={closePaymentPopup}
          paymentObject={createPaymentObject()}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
          paymentType="DONATION"
        />
      )}
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Donate; 